import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subject, first, takeUntil } from 'rxjs';
import { ILanguage } from '@www/core/language.model';
import { LanguageService } from '@www/core/language.service';
import { RouteLanguageService } from '../../services/route-language.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentationService } from '@www-src/app/views/documentation/services/documentation.service';

/**
 * Select language component.
 */
@Component({
    selector: 'app-select-language, [app-select-language]',
    templateUrl: './select-language.component.html',
    styleUrls: ['./select-language.component.scss'],
    standalone: false
})
export class SelectLanguageComponent implements OnInit, OnDestroy {
  @Input() popularLanguages: ILanguage[];
  @Input() filteredLanguages: ILanguage[];
  @Input() locale: string;
  @Output() languageChange = new EventEmitter<void>();

  public selectedLocale: string;
  public isListOpened = false;

  private destroyed$ = new Subject<void>();
  private closeDropdownTimeout?: ReturnType<typeof setTimeout>;
  private currentScreenSize: string;
  private isCurrentScreenSizeSmall = false;
  private displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);

  constructor(
    private languageService: LanguageService,
    breakpointObserver: BreakpointObserver,
    private routeLanguageService: RouteLanguageService,
    private router: Router,
    private route: ActivatedRoute,
    private documentationService: DocumentationService
  ) {
    breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(result => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize = this.displayNameMap.get(query) ?? 'Unknown';
            this.isCurrentScreenSizeSmall = this.currentScreenSize === this.displayNameMap.get(Breakpoints.XSmall) || this.currentScreenSize === this.displayNameMap.get(Breakpoints.Small) || this.currentScreenSize === this.displayNameMap.get(Breakpoints.Medium);
          }
        }
      });
  }

  /**
   * Displays the selected language.
  */
  ngOnInit(): void {
    this.selectedLocale = this.locale;

    this.routeLanguageService.routeLanguage
      .pipe(first())
      .subscribe(language => {
        const id = language ? language : 'en';
        const languages = (this.filteredLanguages ?? []).concat(this.popularLanguages ?? []);
        const currentLanguage = languages.find(item => item.id === id);

        this.selectLanguage(languages.find(item => item.id === id) ?? { id: 'en', language: 'English', isSelected: false }, true)

        if (!currentLanguage) {
          // this.router.navigate([this.router.url.replace(language, '')]);
        }
      });
  }

  ngOnDestroy(): void {
    this.cancelDropdownClose();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Selects site language.
   */
  public selectLanguage(item: ILanguage, isComeFromUrl = false): void {
    if (this.locale !== item.id) {
      this.popularLanguages.map(l => {
        if (l.isSelected) {
          l.isSelected = false;
        }
      });

      this.filteredLanguages.map(l => {
        if (l.isSelected) {
          l.isSelected = false;
        }
      });

      item.isSelected = true;
      this.selectedLocale = item.id;

      this.locale = this.selectedLocale;

      this.languageService.setLanguage(this.selectedLocale);

      this.languageChange.emit();
      this.isListOpened = false;

      if (!isComeFromUrl) {
        this.routeLanguageService.routeLanguage
          .pipe(first())
          .subscribe((previousLanguage: string) => {
            if (previousLanguage) {
              if (item.id === 'en') {
                if (Object.keys(this.route.snapshot.queryParams).length) {
                  this.router.navigate([this.router.url.split('?')[0].replace(previousLanguage, '')], { queryParams: this.route.snapshot.queryParams })
                } else {
                  this.router.navigate([this.router.url.replace(previousLanguage, '')])
                }
              } else {
                if (previousLanguage !== 'en') {
                  if (Object.keys(this.route.snapshot.queryParams).length) {
                    this.router.navigate([this.router.url.split('?')[0].replace(previousLanguage, item.id)], { queryParams: this.route.snapshot.queryParams })
                  } else {
                    this.router.navigate([this.router.url.replace(previousLanguage, item.id)])
                  }
                } else {
                  if (Object.keys(this.route.snapshot.queryParams).length) {
                    this.router.navigate([`${item.id}${this.router.url.split('?')[0]}`], { queryParams: this.route.snapshot.queryParams })
                  } else {
                    this.router.navigate([`${item.id}${this.router.url}`])
                  }
                }
              }
              this.routeLanguageService.routeLanguage.next(item.id);

              this.documentationService
                .getTegsAndCategoryForMd('docs', item.id)
                .subscribe(res => this.documentationService.docsFromJson.next(res))

              this.documentationService
                .getTegsAndCategoryForMd('blog', item.id)
                .subscribe(res => this.documentationService.blogFromJson.next(res))

            }
          })
      }
    }
  }

  public openWithMouseOver(): void {
    if (this.isCurrentScreenSizeSmall) return;
    this.cancelDropdownClose();
    this.isListOpened = true;
  }

  public scheduleDropdownClose(): void {
    if (this.isCurrentScreenSizeSmall) return;
    this.cancelDropdownClose();
    this.closeDropdownTimeout = setTimeout(() => {
      this.isListOpened = false;
      this.closeDropdownTimeout = undefined;
    }, 120);
  }

  public cancelDropdownClose(): void {
    if (this.closeDropdownTimeout) {
      clearTimeout(this.closeDropdownTimeout);
      this.closeDropdownTimeout = undefined;
    }
  }

  public closeDropdown(): void {
    this.cancelDropdownClose();
    this.isListOpened = false;
  }

  public toggleWithClick(): void {
    this.isListOpened = !this.isListOpened;
  }
}
